import { FormProvider, useForm } from 'react-hook-form';
import { IOrgChart } from '@/states/organization.state';
import { useEffect } from 'react';
import FormBuilderWithLayout from '@/components/company/FormBuilderWithLayout';

interface Props {
  formMode: 'add' | 'edit' | 'add-child' | null;
  selectedNode: IOrgChart | null;
  onCancel: () => void;
  onSave: (data: IOrgChart, mode: 'add' | 'edit' | 'add-child', parentId?: number) => Promise<void>;
  autoFocus?: boolean;
}

export default function OrgChartForm({ formMode, selectedNode, onCancel, onSave, autoFocus = false }: Props) {
  const formControl = useForm<IOrgChart>({
    defaultValues: selectedNode || { name: '', description: '' },
  });
  const { handleSubmit, reset } = formControl;

  useEffect(() => {
    if (formMode === 'edit' && selectedNode) {
      reset({
        id: selectedNode.id,
        name: selectedNode.name,
        description: selectedNode.description || '',
      });
    } else if (formMode === 'add' || formMode === 'add-child') {
      reset({ name: '', description: '' });
    }
  }, [formMode, selectedNode, reset]);

  if (!formMode) return null;

  let title: string;
  if (formMode === 'edit') {
    title = 'Edit detail';
  } else if (formMode === 'add') {
    title = 'Create Parent Node';
  } else {
    title = 'Create Child Node';
  }

  let submitLabel = 'Add Child';
  if (formMode === 'edit') submitLabel = 'Save changes';
  else if (formMode === 'add') submitLabel = 'Save';

  return (
    <div className="bg-white border rounded-lg w-full h-full overflow-auto">
      <h2 className="text-lg font-semibold p-5">{title}</h2>
      <div className="mx-5 border-b border-gray-200" />

      <FormProvider {...formControl}>
        <form
          onSubmit={handleSubmit(data => {
            const payload = formMode === 'edit' && selectedNode ? { ...data, id: selectedNode.id } : data;

            onSave(payload, formMode, formMode === 'add-child' ? selectedNode?.id : undefined);
          })}
          className="space-y-6"
        >
          <FormBuilderWithLayout
            buttonAlign="right"
            description=""
            autoFocusField={autoFocus ? 'name' : undefined}
            fields={[
              {
                name: 'name',
                label: 'Role',
                type: 'text',
                placeholder: 'Enter role name',
                required: true,
                colSpan: 2,
              },
              {
                name: 'description',
                label: 'Details',
                type: 'richtext',
                placeholder: 'Add notes or SOP description here...',
                colSpan: 2,
              },
            ]}
            onSubmit={handleSubmit(data =>
              onSave(data, formMode, formMode === 'add-child' ? selectedNode?.id : undefined),
            )}
            onCancel={onCancel}
            submitLabel={submitLabel}
            cancelLabel="Cancel"
            showPublishButton={false}
          />
        </form>
      </FormProvider>
    </div>
  );
}
